import React, { useEffect, useState } from 'react';
import { useApi, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { Header, Content } from '@backstage/core-components';
import { Typography, Grid, Paper, Button, Avatar, Divider, useTheme } from '@material-ui/core';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import {
  Home as HomeIcon,
  Extension as ExtensionIcon,
  LibraryBooks,
  AddCircleOutline as CreateComponentIcon,
  Build as DevOpsIcon,
  Chat as SlackIcon,
} from '@material-ui/icons';

type UserProfile = {
  displayName?: string;
  picture?: string;
};

const HomePage: React.FC = () => {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const theme = useTheme();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<UserProfile[]>([]);

  useEffect(() => {
    document.title = 'Home';

    const fetchProfile = async () => {
      try {
        const userProfile = await microsoftAuth.getProfile();
        setProfile(userProfile ?? null);
      } catch (e) {
        console.error('Failed to fetch user profile', e);
      }
    };
    fetchProfile();
  }, [microsoftAuth]);

  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        const response = await fetch('/api/online-users');
        const users = await response.json();
        setOnlineUsers(users);
      } catch (e) {
        console.error('Failed to fetch online users', e);
      }
    };
    fetchOnlineUsers();
  }, []);

  const usersWithProfile = profile
    ? [profile, ...onlineUsers.filter((u) => u.displayName !== profile.displayName)]
    : onlineUsers;

  const maxVisibleAvatars = 7;
  const visibleUsers = usersWithProfile.slice(0, maxVisibleAvatars);
  const extraUsersCount = usersWithProfile.length > maxVisibleAvatars ? usersWithProfile.length - maxVisibleAvatars : 0;

  return (
    <>
      <Header
        title="iConstruye Backstage" 
        style={{ textAlign: 'center' }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <AvatarGroup max={7} style={{ position: 'absolute', right: '60px' }}>
            {visibleUsers.map((user, index) => (
              <Avatar key={index} alt={user.displayName} src={user.picture} title={user.displayName} />
            ))}
            {extraUsersCount > 0 && <Avatar title={`Más ${extraUsersCount} usuarios`}>+{extraUsersCount}</Avatar>}
          </AvatarGroup>
        </div>
      </Header>
      <Content>
        <Typography
          variant="h5"
          align="center"
          style={{
            margin: '24px auto',
            fontSize: '1.1em',
            lineHeight: 1.6,
            fontWeight: 'bold',
            backgroundColor: theme.palette.background.paper,
            padding: '12px 16px',
            borderRadius: '8px',
            maxWidth: '50%',
            color: 'inherit',
          }}
        >
          En <span style={{ color: theme.palette.primary.main }}>ICONSTRUYE</span>, hemos creado este portal para que nuestros desarrolladores puedan acceder fácilmente a{' '}
          <span style={{ fontWeight: 'bold' }}> documentación</span>, <span style={{ fontWeight: 'bold' }}>APIs</span> y <span style={{ fontWeight: 'bold' }}>herramientas clave</span>, promoviendo una mayor{' '}
          <span style={{ fontWeight: 'bold' }}>autonomía</span> y <span style={{ fontWeight: 'bold' }}>eficiencia</span> en sus proyectos.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <HomeIcon fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Catálogo de Servicios</Typography>
                <Typography variant="body2">Explora y gestiona todos los servicios disponibles en nuestra organización.</Typography>
              </div>
              <div>
                <Button href="/catalog" variant="outlined" style={{ marginRight: '10px' }}>Ir al Catálogo</Button>
                <Button href="/catalog?filter=starred&filters%5Bkind%5D=component&filters%5Buser%5D=starred" variant="outlined">Ver Mis Favoritos</Button>
              </div>
            </Paper>
          </Grid>

          <Divider />

          <Grid item xs={12}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <ExtensionIcon fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>APIs Registradas</Typography>
                <Typography variant="body2">Accede a la lista completa de APIs registradas y consulta detalles específicos.</Typography>
              </div>
              <div>
                <Button href="/api-docs" variant="outlined" style={{ marginRight: '10px' }}>Explorar APIs</Button>
                <Button href="/api-docs?filter=starred&filters%5Bkind%5D=api&filters%5Buser%5D=starred" variant="outlined">Mis APIs Favoritas</Button>
              </div>
            </Paper>
          </Grid>

          <Divider />

          <Grid item xs={12}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <LibraryBooks fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Documentación</Typography>
                <Typography variant="body2">Consulta la documentación técnica y guía sobre nuestros servicios y APIs.</Typography>
              </div>
              <div>
                <Button href="/docs" variant="outlined" style={{ marginRight: '10px' }}>Ver Documentación</Button>
                <Button href="/docs?filter=starred&filters%5Buser%5D=starred" variant="outlined">Mis Documentos Favoritos</Button>
              </div>
            </Paper>
          </Grid>

          <Divider />

          <Grid item xs={12}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <CreateComponentIcon fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Registrar Componente</Typography>
                <Typography variant="body2">Añade nuevos componentes a nuestro sistema para mejorar la colaboración.</Typography>
              </div>
              <Button href="/create" variant="outlined">Crear Componente</Button>
            </Paper>
          </Grid>

          <Divider />

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <DevOpsIcon fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Azure DevOps</Typography>
                <Typography variant="body2">Accede a nuestras herramientas de DevOps para el desarrollo y gestión de proyectos.</Typography>
              </div>
              <Button href="https://dev.azure.com/iccorpiconstruye" variant="outlined" target="_blank" rel="noopener noreferrer">
                Ir a DevOps
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              style={{ display: 'flex', alignItems: 'center', padding: '20px', marginBottom: '12px', transition: 'transform 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <SlackIcon fontSize="large" style={{ marginRight: '20px' }} />
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>Slack</Typography>
                <Typography variant="body2">Comunícate con el equipo y mantente al tanto de todas las actualizaciones.</Typography>
              </div>
              <Button href="https://iconstruye.slack.com" variant="outlined" target="_blank" rel="noopener noreferrer">
                Ir a Slack
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Content>
    </>
  );
};

export default HomePage;
